import { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";
import { formatNOK } from "@/lib/format";

export interface BudgetLineOption {
  id: string;
  description: string | null;
  phase: string | null;
  cost_type: string | null;
  template_id: string | null;
}

export interface AllocationRowState {
  budget_line_id: string | null;
  amount: number;
  description: string | null;
  po_account_id: number | null;
  source: "po" | "manual" | "vision";
}

interface Props {
  row: AllocationRowState;
  budgetLines: BudgetLineOption[];
  onChange: (patch: Partial<AllocationRowState>) => void;
  onRemove: () => void;
}

/** Én redigerbar post-rad med søkbar budsjettlinje-velger (jf. TemplatePicker). */
export function VoucherAllocationRow({
  row,
  budgetLines,
  onChange,
  onRemove,
}: Props) {
  const [open, setOpen] = useState(false);
  const selected = budgetLines.find((b) => b.id === row.budget_line_id) ?? null;

  return (
    <div className="flex items-center gap-2 py-2 border-b last:border-b-0">
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">
          {row.description || (
            <span className="text-muted-foreground italic">Uten beskrivelse</span>
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          {formatNOK(row.amount)}
          {row.po_account_id != null && ` · konto ${row.po_account_id}`}
        </div>
      </div>

      {row.source === "manual" && (
        <Input
          type="number"
          aria-label="Beløp"
          className="w-28 h-8 text-sm"
          value={row.amount}
          onChange={(e) => onChange({ amount: Number(e.target.value) || 0 })}
        />
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "inline-flex items-center gap-1 max-w-[12rem] truncate text-xs px-2 py-1 rounded border hover:bg-muted",
              selected ? "text-foreground" : "text-yellow-700 border-yellow-300",
            )}
            title={selected ? "Klikk for å bytte linje" : "Velg budsjettlinje"}
          >
            {!selected && (
              <span
                className="inline-flex w-2 h-2 rounded-full bg-yellow-500 shrink-0"
                aria-hidden
              />
            )}
            <span className="truncate">
              {selected ? selected.description || "(uten navn)" : "Velg linje..."}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-72" align="end">
          <Command>
            <CommandInput placeholder="Søk i budsjettlinjer..." className="h-9" />
            <CommandList className="max-h-72 overscroll-contain">
              <CommandEmpty>Ingen linjer matcher søket.</CommandEmpty>
              <CommandGroup heading={`${budgetLines.length} linjer`}>
                {budgetLines.map((b) => (
                  <CommandItem
                    key={b.id}
                    value={`${b.description ?? ""} ${b.phase ?? ""}`}
                    onSelect={() => {
                      onChange({ budget_line_id: b.id });
                      setOpen(false);
                    }}
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="text-xs truncate">
                        {b.description || "(uten navn)"}
                      </span>
                      <span className="text-[10px] text-muted-foreground ml-2 shrink-0">
                        {b.phase}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0"
        onClick={onRemove}
        aria-label="Fjern post"
      >
        <SrIcon name="trash" className="w-4 h-4" />
      </Button>
    </div>
  );
}
